import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

/**
 * Long-form typography primitives.
 *
 * Deliberately hand-built rather than @tailwindcss/typography: the plugin isn't
 * installed, and DESIGN.md's type scale + Obangsaek link colour would need
 * overriding on almost every element anyway.
 *
 * Reading measure is 672px (max-w-article) per DESIGN.md.
 */

interface Props {
  children: ReactNode;
  className?: string;
  id?: string;
}

/** Section heading — pairs an English title with its Korean equivalent. */
export function H2({ children, ko, id, className }: Props & { ko?: string }) {
  return (
    <h2
      id={id}
      className={twMerge(
        'scroll-mt-24 mt-12 mb-4 text-2xl font-bold leading-tight text-ink-900 dark:text-ink-50',
        className
      )}
    >
      {children}
      {ko && (
        <span className="mt-1 block font-korean text-base font-medium text-ink-500 dark:text-ink-400">
          {ko}
        </span>
      )}
    </h2>
  );
}

export function H3({ children, id, className }: Props) {
  return (
    <h3
      id={id}
      className={twMerge(
        'scroll-mt-24 mt-8 mb-3 text-xl font-semibold leading-snug text-ink-900 dark:text-ink-50',
        className
      )}
    >
      {children}
    </h3>
  );
}

export function P({ children, className }: Props) {
  return (
    <p className={twMerge('mb-5 text-base leading-7 text-ink-700 dark:text-ink-200', className)}>
      {children}
    </p>
  );
}

/** Opening paragraph — larger, sets the stakes of the article. */
export function Lead({ children, className }: Props) {
  return (
    <p className={twMerge('mb-8 text-lg leading-8 text-ink-700 dark:text-ink-200', className)}>
      {children}
    </p>
  );
}

export function UL({ children, className }: Props) {
  return (
    <ul className={twMerge('mb-5 space-y-2 pl-1', className)}>{children}</ul>
  );
}

export function OL({ children, className }: Props) {
  return (
    <ol className={twMerge('mb-5 list-inside list-decimal space-y-2 pl-1', className)}>
      {children}
    </ol>
  );
}

export function LI({ children, className }: Props) {
  return (
    <li
      className={twMerge(
        'relative pl-6 text-base leading-7 text-ink-700 dark:text-ink-200',
        'before:absolute before:left-0 before:top-[0.6em] before:h-1.5 before:w-1.5',
        'before:rounded-full before:bg-jeok-500 dark:before:bg-jeok-dark',
        className
      )}
    >
      {children}
    </li>
  );
}

/** Numbered list item that keeps the marker aligned with the reading measure. */
export function OLI({ children, className }: Props) {
  return <li className={twMerge('text-base leading-7 text-ink-700 dark:text-ink-200', className)}>{children}</li>;
}

/** Inline emphasis for figures and rates — tabular numerals keep columns honest. */
export function Num({ children, className }: Props) {
  return (
    <span className={twMerge('font-mono text-[0.95em] tabular-nums', className)}>{children}</span>
  );
}

export function Strong({ children, className }: Props) {
  return (
    <strong className={twMerge('font-semibold text-ink-900 dark:text-ink-50', className)}>
      {children}
    </strong>
  );
}

/**
 * Cheong-blue link per DESIGN.md.
 *
 * Internal hrefs render as a router <Link> so in-article cross-links stay
 * client-side — a full page reload between guides would throw away the whole
 * point of internal linking for engagement time.
 */
export function A({
  href,
  children,
  className,
}: Props & { href: string }) {
  const styles = twMerge(
    'font-medium text-cheong-500 underline decoration-cheong-500/30 underline-offset-2',
    'transition-colors hover:decoration-cheong-500 dark:text-cheong-dark dark:decoration-cheong-dark/30',
    className
  );

  const isInternal = href.startsWith('/') && !href.startsWith('//');

  if (isInternal) {
    return (
      <Link to={href} className={styles}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow" className={styles}>
      {children}
    </a>
  );
}

/** Pull quote / statistic highlight. */
export function Stat({ value, label, className }: { value: string; label: string; className?: string }) {
  return (
    <div className={twMerge('my-8 border-l-4 border-jeok-500 pl-5 dark:border-jeok-dark', className)}>
      <div className="font-mono text-3xl font-bold tabular-nums leading-tight text-ink-900 dark:text-ink-50">
        {value}
      </div>
      <div className="mt-1 text-sm text-ink-500 dark:text-ink-400">{label}</div>
    </div>
  );
}
